import { Injectable } from '@angular/core';
import { concatMap, EMPTY, filter, forkJoin, map, Observable, of, ReplaySubject, Subject, switchMap, withLatestFrom } from 'rxjs';
import { catchError, distinctUntilChanged, take, tap } from 'rxjs/operators';
import { MainTabIndices } from '../enums/main-tab-indices.enum';
import { DocViewerStore } from '@store/legacy/doc-viewer.store';
import { ReviewSetService } from '@services/review-set.service';
import { DocFile } from '@models/doc-file.model';
import { FileType } from '@models/file-type';
import { cloneDeep } from 'lodash-es';
import { iFrameUrlFactory } from '@utilities/iframe-url-factory';
import { DocViewerTab } from '../enums/doc-viewer-tab.enum';
import { OkCancelDialogComponent } from '../components/dialogs/ok-cancel/ok-cancel-dialog.component';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { LexbeDoc } from '@models/doc.model';
import { DocViewerFormEngine } from '../engines/doc-viewer-form.engine';
import { ProductionManager } from '@utilities/production-manager';
import { ReviewSdk } from '../sdks/review.sdk';
import { FactResourceToFactMapper } from '@models/mappers/fact-resource-to-fact.mapper';
import { ReviewEntryResourceToReviewEntryMapper } from '@models/mappers/review-entry-resource-to-review-entry.mapper';
import { IssueResourceToIssueMapper } from '@models/mappers/issue-resource-to-issue.mapper';
import { RedactionAnnotationCreatePdfRequest } from '../components/review/redaction-annotation/redaction-annotation-create-pdf-request.model';
import { RedactionAnnotationRequestSdk } from '../sdks/redaction-annotation-request.sdk';
import { NotificationManager } from './notification.manager';
import { Router } from '@angular/router';
import { DocNoteService } from '@services/doc-note.service';
import { InsightsUtility } from '../utilities/insights/insights.utility';
import { DocViewerAccessDeniedError } from '../insights/errors/doc-viewer-access-denied.error';
import { OkCancelDialogModel } from '../components/doc-viewer-new/dialogs/ok-cancel-dialog-new/ok-cancel-dialog.model';
import { OkCancelDialogNewComponent } from '../components/doc-viewer-new/dialogs/ok-cancel-dialog-new/ok-cancel-dialog-new.component';
import { currentRedactionEditor } from '@store/doc-viewer/redaction-editor/redaction-editor.selectors';
import { Store } from '@ngrx/store';
import { currentAnnotationEditor } from '@store/doc-viewer/annotation-editor/annotation-editor.selectors';
import { DocNote } from '../components/doc-viewer-new/doc-viewer-bottom-right-bar/doc-note-panel/doc-note.model';
import { redactionEditorActions } from '@store/doc-viewer/redaction-editor/redaction-editor.actions';
import { annotationEditorActions } from '@store/doc-viewer/annotation-editor/annotation-editor.actions';

const factResourceToFactMapper = new FactResourceToFactMapper();
const reviewEntryResourceToReviewEntryMapper = new ReviewEntryResourceToReviewEntryMapper();
const issueResourceToIssueMapper = new IssueResourceToIssueMapper();

@Injectable({
	'providedIn': 'root'
})
export class ReviewManager {
	public saveRequests$: Observable<void>;
	private documentIdSource: ReplaySubject<DocumentIdChangeRequest> = new ReplaySubject<DocumentIdChangeRequest>(1);
	private docs: string[];
	private saveRequestSource = new Subject<void>();
	private pristineDocument: LexbeDoc;
	private currentIdSnapshot: string;

	constructor(
		private docViewerStore: DocViewerStore,
		private reviewSetService: ReviewSetService,
		private iFrameUrlFactory: iFrameUrlFactory,
		private matDialog: MatDialog,
		private productionManager: ProductionManager,
		private documentViewerFormEngine: DocViewerFormEngine,
		private reviewSdk: ReviewSdk,
		private requestSdk: RedactionAnnotationRequestSdk,
		private docNoteService: DocNoteService,
		private notificationManager: NotificationManager,
		private router: Router,
		private insightsUtility: InsightsUtility,
		private store: Store
	) {
		this.docViewerStore.loadDocList();
		this.docViewerStore.docList$.subscribe((docList) => {
			if (docList) {
				this.docs = docList.map((d) => d.docId);
				this.docViewerStore.updateState((currentState) => {
					if (!this.docs || currentState.currentDocIndex === -1) {
						currentState.navAvailable = false;
					} else
						currentState.navAvailable = true;
					currentState.totalDocs = docList.length;
					return currentState;
				})
			}
		});

		this.saveRequests$ = this.saveRequestSource.asObservable();
		this.documentIdSource.pipe(
			tap((current) => { this.currentIdSnapshot = current.documentId }),
			distinctUntilChanged((previous, current) => {
				if (current.force) {
					return false;
				}
				return previous.documentId === current.documentId;
			}),
			map((change) => change.documentId),
			withLatestFrom(this.docViewerStore.simpleState$),
			switchMap(([documentId, state]) => this.reviewSdk.getDocumentReviewState$(documentId, state.reviewSetId, state.projectChangeSets))
		).subscribe({
			next: (documentReviewState) => {
				const document = documentReviewState.document;
				const reviewStatus = documentReviewState.reviewStatus;
				const filesByType = new Map<FileType, DocFile>();
				documentReviewState.documentInfo.docFiles.filter((f: DocFile) => f.lifecycle === 0).map((f: DocFile) => filesByType.set(f.fileType, f));
				const hasOriginalRendering = filesByType.has(FileType.OriginalFile);
				const hasPdfRendering = filesByType.has(FileType.NormalizedPdf);
				const clone = cloneDeep(document);
				const isChangingDocument = this.pristineDocument?.docId !== clone.docId;
				let hasUnsavedCustomCodingChanges: boolean = false;

				this.docViewerStore.updateState((currentState) => {
					currentState.projectChangeSets = [];
					currentState.currentDocIndex = this.docs ? this.docs.indexOf(document.docId) : null;
					if (!this.docs || currentState.currentDocIndex === -1) {
						currentState.navAvailable = false;
					}
					currentState.totalDocs = this.docs?.length;
					currentState.shouldAutoAdvance = JSON.parse(localStorage.getItem('shouldAutoAdvance') || null);
					currentState.shouldPropagateCoding = JSON.parse(localStorage.getItem('shouldPropagateCoding') || null);
					currentState.hasOriginalRendering = hasOriginalRendering;
					currentState.hasPdfRendering = hasPdfRendering;
					currentState.hasProduction = documentReviewState.hasProducedFiles;
					currentState.hasTranslation = filesByType.has(FileType.NormalizedTranslatedFile);
					currentState.originalSizeMb = hasOriginalRendering ? this.bytesToMb(filesByType.get(FileType.OriginalFile).fileSize) : 0;
					currentState.pdfSizeMb = hasPdfRendering ? this.bytesToMb(filesByType.get(FileType.NormalizedPdf).fileSize) : 0;
					currentState.showOriginalSizeWarning = currentState.originalSizeMb > 5000;
					currentState.showPdfSizeWarning = currentState.pdfSizeMb > 11000;
					currentState.tabPreferences = documentReviewState.tabStates;
					currentState.reviewDocs = reviewEntryResourceToReviewEntryMapper.mapCollection(documentReviewState.reviewDocs);
					currentState.reviewedByMe = reviewStatus === 1 || reviewStatus === 3;
					currentState.reviewedByOthers = reviewStatus === 2 || reviewStatus === 3;
					currentState.reviewedByMe = documentReviewState.isProjection || currentState.reviewedByMe;
					currentState.reviewStatus = reviewStatus;
					currentState.custodians = documentReviewState.custodians;
					currentState.audits = documentReviewState.auditHistory as any;
					currentState.folder = documentReviewState.folder;
					currentState.facts = factResourceToFactMapper.mapCollection(documentReviewState.facts);
					currentState.issues = issueResourceToIssueMapper.mapCollection(documentReviewState.issues);
					currentState.emailThreadGroupingId = documentReviewState.emailThreadGroupingId;
					currentState.splitDoc = documentReviewState.splitDocs;
					currentState.hasUnsavedAnnotationChanges = false;
					currentState.hasUnsavedRedactionChanges = false;
					currentState.hasUnsavedCustomCodingChanges = hasUnsavedCustomCodingChanges = currentState.hasUnsavedCustomCodingChanges;
					if (isChangingDocument || (!isChangingDocument && !currentState.hasUnsavedNotes)) {
						currentState.notes = documentReviewState.notes;
						currentState.hasUnsavedNotes = false;
					}
					this.docViewerStore.updateRedactionEditsSource([]);
					this.docViewerStore.updateAnnotationEditsSource([]);
					currentState.lastChangeSet = documentReviewState.lastCodingChangeSet;
					currentState.remainingReviewDocCount = documentReviewState.remainingReviewDocCount;
					currentState.reviewedByMeCount = documentReviewState.reviewedByMeCount;
					return currentState;
				});

				const hasChanges = hasUnsavedCustomCodingChanges || this.docViewerStore.documentDataFormGroup?.dirty;
				if (isChangingDocument || !hasChanges) {
					this.pristineDocument = cloneDeep(document);
					this.documentViewerFormEngine.createDocumentFormGroup(clone);
					if (documentReviewState.isProjection) {
						this.docViewerStore.documentDataFormGroup.markAsDirty();
					}
				}

				this.docViewerStore.updateCurrentDocumentSource(clone);
			},
			error: (error) => {
				this.insightsUtility.event(new DocViewerAccessDeniedError(error, this.currentIdSnapshot));
				this.router.navigate(['/', 'review', 'access-denied']);
			}
		});

		this.docViewerStore.reset$.subscribe(() => {
			this.docViewerStore.updateState((currentState) => {
				currentState.hasUnsavedCustomCodingChanges = false;
				currentState.hasUnsavedNotes = false;
				return currentState;
			});
		})

		this.docViewerStore.simpleState$.subscribe((current) => {
			if (typeof current.shouldAutoAdvance !== 'undefined') {
				localStorage.setItem('shouldAutoAdvance', JSON.stringify(current.shouldAutoAdvance || false));
			}

			if (typeof current.shouldPropagateCoding !== 'undefined') {
				localStorage.setItem('shouldPropagateCoding', JSON.stringify(current.shouldPropagateCoding || false));
			}
		});

		this.docViewerStore.currentDocument$.pipe(
			withLatestFrom(this.docViewerStore.simpleState$)
		).subscribe(([currentDocument, currentState]) => {
			this.docViewerStore.updateState((updateSnapshot) => {
				updateSnapshot.pages = [];
				updateSnapshot.maxPage = currentDocument.common.pageCount;
				const hasBates = currentDocument.bates.batesPrefix.length > 0 && currentDocument.bates.batesNumberStart !== 0;
				for (let index = 0; index < currentDocument.common.pageCount; index++) {
					let title = (index + 1).toString();
					if (hasBates) {
						title = `${currentDocument.bates.batesPrefix} ${(currentDocument.bates.batesNumberStart + index).toString().padStart(currentDocument.bates.batesNumberDigits, '0')} (p. ${index + 1})`;
					}
					updateSnapshot.pages.push(title);
				}
				updateSnapshot.currentPage = Number(currentState.legacyParams.page);
				return updateSnapshot;
			});
		});

		this.saveRequests$.pipe(
			withLatestFrom(this.docViewerStore.simpleState$, this.docViewerStore.currentDocument$),
			switchMap(([, state, currentDocument]) => this.documentViewerFormEngine.save().pipe(
				switchMap(() => {
					const changes: Observable<void>[] = [];
					if (state.hasUnsavedRedactionChanges) {
						changes.push(this.saveRedactions(currentDocument));
					}
					if (state.hasUnsavedAnnotationChanges) {
						changes.push(this.saveAnnotations(currentDocument));
					}
					if (state.hasUnsavedNotes) {
						changes.push(this.saveNotes(currentDocument));
					}

					return changes.length > 0 ? forkJoin(changes) : of(null);
				}),
				catchError(() => {
					this.notificationManager.displaySnackBarMessage({
						message: 'There was an issue saving the document. Please try again',
						type: 'error',
						ttl: 30000
					})
					return EMPTY;
				})
			)),
			concatMap(() => this.docViewerStore.simpleState$.pipe(take(1))),
			catchError(() => {
				this.notificationManager.displaySnackBarMessage({
					message: 'There was an issue saving the document. Please try again',
					type: 'error',
					ttl: 30000
				})
				return of(null);
			}),
			concatMap((state) => {
				if (!state) return of(null);
				if (state.mode === 'review') {
					return this.loadNextDocumentInReviewSet().pipe(map((loaded) => {
						if (loaded)
							this.contextPanelScrollIntoView();
						else
							this.exitReview();
						return loaded;
					}));
				}
				else if (state.shouldAutoAdvance) {
					return this.goToNextDocument().pipe(map((loaded) => {
						if (!loaded)
							this.refreshDocument();
						return loaded;
					}));
				}
				this.refreshDocument(true);
				return of(null);
			})
		).subscribe();

		this.docViewerStore.currentDocument$.pipe(
			withLatestFrom(this.docViewerStore.simpleState$),
			tap(([, currentState]) => {
				if (!currentState.hasProduction)
					this.productionManager.reset();
			}),
			filter(([, currentState]) => currentState.hasProduction),
			concatMap(([document]) => this.productionManager.load(document.docId))
		).subscribe();
	}

	protected saveRedactions(currentDocumentSnapshot: LexbeDoc): Observable<void> {
		return this.docViewerStore.redactionEdits$.pipe(
			take(1),
			switchMap((edits) => {
				if (currentDocumentSnapshot.status.isRedactionProcessing)
					return of(null);
				const createPdfRequest = new RedactionAnnotationCreatePdfRequest(currentDocumentSnapshot.docId, true, edits);
				return this.requestSdk.applyEditsRequest(createPdfRequest).pipe(
					switchMap(() => {
						return this.docViewerStore.updateState((currentState) => {
							currentState.hasUnsavedRedactionChanges = false;
							return currentState;
						});
					})
				);
			})
		);
	}

	protected saveAnnotations(currentDocumentSnapshot: LexbeDoc): Observable<void> {
		return this.docViewerStore.annotationEdits$.pipe(
			take(1),
			switchMap((edits) => {
				if (currentDocumentSnapshot.status.isAnnotationProcessing)
					return of(null);
				const createPdfRequest = new RedactionAnnotationCreatePdfRequest(currentDocumentSnapshot.docId, false, edits);
				return this.requestSdk.applyEditsRequest(createPdfRequest).pipe(
					switchMap(() => {
						return this.docViewerStore.updateState((currentState) => {
							currentState.hasUnsavedAnnotationChanges = false;
							return currentState;
						});
					})
				);
			})
		);
	}

	protected saveNotes(doc: LexbeDoc): Observable<void> {
		return this.docViewerStore.docNotesEdits$.pipe(
			take(1),
			switchMap((edit) => {
				const note = this.newNote(edit, doc);
				return this.docNoteService.createDocNote(note).pipe(
					switchMap(() => {
						return this.docViewerStore.updateState((currentState) => {
							currentState.hasUnsavedNotes = false;
							return currentState;
						});
					})
				);

			})
		);
	}

	public loadDocument(documentId: string): void {
		this.documentIdSource.next({ documentId });
	}

	public refreshDocument(resetForm: boolean = false): void {
		this.documentIdSource.pipe(
			take(1)
		).subscribe((change) => {
			if (resetForm) {
				this.docViewerStore.updateState((snapshot) => {
					snapshot.hasUnsavedAnnotationChanges = false;
					snapshot.hasUnsavedRedactionChanges = false;
					snapshot.hasUnsavedCustomCodingChanges = false;
					snapshot.hasUnsavedNotes = false;

					return snapshot;
				});
				this.docViewerStore.reset();
			}
			this.documentIdSource.next({ force: true, documentId: change.documentId });
		});
	}

	public changeCurrentTab(tab: MainTabIndices): void {
		this.docViewerStore.updateCurrentTabSource(tab);
	}

	public goToPreviousDocument(): Observable<boolean> {
		return this.continueWithoutSavingConfirmationDialog().pipe(
			switchMap((confirm) => {
				if (confirm) {
					return this.docViewerStore.simpleState$.pipe(
						take(1),
						switchMap((currentState) => {
							if (currentState.mode === 'viewer' && currentState.currentDocIndex > 0) {
								const targetDocumentIndex = --currentState.currentDocIndex;
								const previousDocumentId = this.docs[targetDocumentIndex];
								this.loadDocument(previousDocumentId);
								return of(true);
							}
							if (currentState.mode === 'review' && currentState.reviewDocs) {
								return this.docViewerStore.currentDocument$.pipe(
									take(1),
									switchMap((document) => {
										const currentReviewEntry = currentState.reviewDocs.find(x => x.docId === document.docId);
										const previousReviewEntry = currentState.reviewDocs.find(x => x.reviewOrder === currentReviewEntry.reviewOrder - 1);
										if (previousReviewEntry) {
											this.loadDocument(previousReviewEntry.docId);
											return of(true);
										}
										return of(false);
									})
								);
							}
							return of(false);
						}));
				}
				return of(false);
			}));
	}

	public goToNextDocument(): Observable<boolean> {
		return this.continueWithoutSavingConfirmationDialog().pipe(
			switchMap((confirm) => {
				if (confirm) {
					return this.docViewerStore.simpleState$.pipe(
						take(1),
						switchMap((currentState) => {
							if (currentState.mode === 'viewer' && currentState.currentDocIndex < currentState.totalDocs - 1) {
								const targetDocumentIndex = ++currentState.currentDocIndex;
								const nextDocumentId = this.docs[targetDocumentIndex];
								this.loadDocument(nextDocumentId);
								return of(true);
							}
							if (currentState.mode === 'review' && currentState.reviewDocs) {
								return this.docViewerStore.currentDocument$.pipe(
									take(1),
									switchMap((document) => {
										const currentReviewEntry = currentState.reviewDocs.find(x => x.docId === document.docId);
										const nextReviewEntry = currentState.reviewDocs.find(x => x.reviewOrder === currentReviewEntry.reviewOrder + 1);
										if (nextReviewEntry) {
											this.loadDocument(nextReviewEntry.docId);
											return of(true);
										}
										return of(false);
									})
								);
							}
							return of(false);
						}));
				}
				return of(false);
			}));
	}

	public loadNextDocumentInReviewSet(): Observable<boolean> {
		return this.continueWithoutSavingConfirmationDialog().pipe(
			switchMap((confirm) => {
				if (confirm) {
					return this.docViewerStore.simpleState$.pipe(
						take(1),
						switchMap((currentState) => {
							if (currentState.mode === 'review') {
								return this.docViewerStore.currentDocument$.pipe(
									take(1),
									switchMap((document) => this.reviewSetService.getNextReviewDoc(currentState.reviewSetId, document.docId).pipe(
										map((nextDocumentId) => {
											if (nextDocumentId) {
												this.loadDocument(nextDocumentId);
												return true;
											}
											return false;
										})
									))
								);
							}
							return of(false);
						}));
				}
				return of(false);
			}));
	}

	public goToDocumentBelow(skipAttachment: boolean = false): Observable<boolean> {
		return this.continueWithoutSavingConfirmationDialog().pipe(
			switchMap((confirm) => {
				if (confirm) {
					return this.docViewerStore.simpleState$.pipe(
						take(1),
						switchMap((currentState) => {
							if (currentState.mode === 'review' && currentState.reviewDocs) {
								return this.docViewerStore.currentDocument$.pipe(
									take(1),
									switchMap((document) => {
										const reviewSet = currentState.reviewDocs.filter(x => {
											if (skipAttachment)
												return x.relationship !== 'child';
											return true;
										})
										let currentReviewIndex = reviewSet.findIndex(x => x.docId === document.docId);
										if (currentReviewIndex === -1) {
											const currentReviewEntry = currentState.reviewDocs.find(x => x.docId === document.docId);
											currentReviewIndex = reviewSet.findIndex(x => x.docId === currentReviewEntry.emailParentId);
										}
										const targetReviewIndex = currentReviewIndex + 1;
										if (targetReviewIndex > -1 && targetReviewIndex < currentState.reviewDocs.length) {
											this.loadDocument(reviewSet[targetReviewIndex].docId);
											return of(true);
										}
										return of(false);
									})
								);
							}
							return of(false);
						}));
				}
				return of(false);
			}));
	}

	public goToDocumentAbove(skipAttachment: boolean = false): Observable<boolean> {
		return this.continueWithoutSavingConfirmationDialog().pipe(
			switchMap((confirm) => {
				if (confirm) {
					return this.docViewerStore.simpleState$.pipe(
						take(1),
						switchMap((currentState) => {
							if (currentState.mode === 'review' && currentState.reviewDocs) {
								return this.docViewerStore.currentDocument$.pipe(
									take(1),
									switchMap((document) => {
										const reviewSet = currentState.reviewDocs.filter(x => {
											if (skipAttachment)
												return x.relationship !== 'child';
											return true;
										})
										let currentReviewIndex = reviewSet.findIndex(x => x.docId === document.docId);
										if (currentReviewIndex === -1) {
											const currentReviewEntry = currentState.reviewDocs.find(x => x.docId === document.docId);
											currentReviewIndex = reviewSet.findIndex(x => x.docId === currentReviewEntry.emailParentId) + 1;
										}
										const targetReviewIndex = currentReviewIndex - 1;
										if (targetReviewIndex > -1 && targetReviewIndex < currentState.reviewDocs.length) {
											this.loadDocument(reviewSet[targetReviewIndex].docId);
											return of(true);
										}
										return of(false);
									})
								);
							}
							return of(false);
						}));
				}
				return of(false);
			}));
	}


	public requestSave(): void {
		this.saveRequestSource.next();
	}

	public goToPage(pageNumber: number): void {
		this.docViewerStore.updateState((current) => {
			current.currentPage = pageNumber;
			return current;
		}).pipe(
			switchMap(() => this.iFrameUrlFactory.updateContent(DocViewerTab.Page))
		).subscribe(() => {
			this.changeCurrentTab(MainTabIndices.Page);
		});
	}

	public hasUnsavedChanges(): Observable<boolean> {
		return this.docViewerStore.simpleState$.pipe(
			take(1),
			withLatestFrom(
				this.store.select(currentRedactionEditor()),
				this.store.select(currentAnnotationEditor)),
			map(([currentState, redactionEditor, annotationEditor]) => {
				return currentState?.hasUnsavedRedactionChanges
					|| currentState?.hasUnsavedAnnotationChanges
					|| this.docViewerStore?.documentDataFormGroup?.dirty
					|| currentState?.hasUnsavedCustomCodingChanges
					|| currentState?.hasUnsavedNotes
					|| redactionEditor?.hasUnsavedRedactionChanges
					|| annotationEditor?.hasUnsavedAnnotationChanges;
			}));
	}

	public continueWithoutSavingConfirmationDialog(): Observable<boolean> {
		return this.hasUnsavedChanges().pipe(
			switchMap((unsavedChanges) => {
				if (unsavedChanges) {
					const useNewDialog: boolean = sessionStorage['useNewDocViewer'] ? sessionStorage['useNewDocViewer'] === 'true' : true;
					if (useNewDialog) {
						const model = new OkCancelDialogModel();
						model.title = 'Confirm';
						model.message = 'Continue without saving changes?';
						const config = new MatDialogConfig<OkCancelDialogModel>();
						config.data = model;
						return this.matDialog.open(OkCancelDialogNewComponent, config)
							.afterClosed()
							.pipe(switchMap((confirm) => {
								if (confirm){
									this.store.dispatch(redactionEditorActions.markEditorClean());
									this.store.dispatch(annotationEditorActions.markEditorClean());
									return of(true);
								}
								return EMPTY;
							}));
					}
					else {
						return this.matDialog.open(OkCancelDialogComponent,
							{
								data: {
									title: 'Confirm',
									message: 'Continue without saving changes?'
								}
							})
							.afterClosed()
							.pipe(switchMap((confirm) => {
								if (confirm)
									return of(true);
								return EMPTY;
							}));
					}
				}
				return of(true);
			}));
	}

	public exitReview(): void {
		this.continueWithoutSavingConfirmationDialog().subscribe((confirm) => {
			if (confirm)
				this.router.navigate(['/', 'discovery', 'review-sets']);
		});
	}

	public contextPanelScrollIntoView(ms: number = 1000) {
		setTimeout(() => {
			const activeRow = document.getElementById('context-panel-main').querySelector('.active');
			activeRow.scrollIntoView({ behavior: 'smooth', block: 'center' });
		}, ms);
	}

	private bytesToMb(bytes: number): number {
		return bytes / (1024 * 1024);
	}

	private newNote(existingNote: DocNote, doc: LexbeDoc): DocNote {
		let docTitle = doc.common.docTitle.substring(0, 149);
		if (doc.common.docTitle.length > 150) {
			docTitle += "...";
		}

		const docNote = new DocNote();
		docNote.docId = doc.docId;
		docNote.docNoteTitle = `Note for "${docTitle}"`;
		docNote.pageNumber = existingNote?.pageNumber ?? 0;
		docNote.docNote = existingNote.docNote;
		docNote.isPrivate = false;
		return docNote;
	}
}

interface DocumentIdChangeRequest {
	force?: boolean;
	documentId: string;
}
